import { useEffect, useState } from 'react';

/**
 * Returns a copy of `value` that only updates after it has stopped changing
 * for `delayMs`. The one place debouncing lives — every search box across the
 * apps used to hand-roll its own `useState` + `setTimeout` pair, which is how
 * they drifted to different delays.
 *
 * @param value - The fast-changing value (e.g. an input's text).
 * @param delayMs - Quiet period before the debounced value catches up. Default 300.
 * @returns The debounced value.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
